<script setup>
import { ref, computed, watch, getCurrentInstance } from "vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import { FreeMode, Navigation, Thumbs } from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/thumbs";

import imagenDefault from "@/assets/img/logos/subasta-lote-en-espera.png";

const props = defineProps({
  LoteStore: Object,
});

const cargaCompleta = ref(false);
const { appContext } = getCurrentInstance();
const storage_path = appContext.config.globalProperties.storage_path;

watch(
  () => props.LoteStore.lote?.multimedia,
  (multimedia) => {
    if (Array.isArray(multimedia)) {
      cargaCompleta.value = true;
    }
  },
  { immediate: true }
);

const thumbsSwiper = ref(null);
const modules = [FreeMode, Navigation, Thumbs];

const setThumbsSwiper = (swiper) => {
  thumbsSwiper.value = swiper;
};

function obtenerUrlYoutube(url) {
  // eslint-disable-next-line
  const regex = /(?:youtube\.com\/(?:[^\/\n\s]+\/(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^&\n?#]+)/;
  const match = url.match(regex);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
}

const multimediaWithPlaceholders = computed(() => {
  const multimedia = props.LoteStore.lote?.multimedia || [];
  const placeholdersNeeded = Math.max(5 - multimedia.length, 0);
  const placeholders = Array.from({ length: placeholdersNeeded }, (_, index) => ({
    placeholder: true,
    id: `placeholder-${index}`,
  }));
  return [...multimedia, ...placeholders];
});

function handleMouseMove(e) {
  const zoomImg = e.currentTarget.querySelector(".zoom-pan");
  const rect = e.currentTarget.getBoundingClientRect();
  const x = ((e.clientX - rect.left) / rect.width) * 100;
  const y = ((e.clientY - rect.top) / rect.height) * 100;
  if (zoomImg) {
    zoomImg.style.transformOrigin = `${x}% ${y}%`;
    zoomImg.style.transform = "scale(2)";
  }
}

function resetZoom(e) {
  const zoomImg = e.currentTarget.querySelector(".zoom-pan");
  if (zoomImg) {
    zoomImg.style.transform = "scale(1)";
    zoomImg.style.transformOrigin = "center center";
  }
}

function scrollThumbs(direction) {
  const swiper = thumbsSwiper.value;
  if (!swiper) return;
  const perView = swiper.params.slidesPerView;
  const totalSlides = swiper.slides.length;
  const currentIndex = swiper.activeIndex;
  const nextIndex =
    direction === "up"
      ? Math.max(currentIndex - perView, 0)
      : Math.min(currentIndex + perView, totalSlides - perView);
  swiper.slideTo(nextIndex);
}

function onThumbsScroll(event) {
  event.preventDefault();
  if (!thumbsSwiper.value) return;

  if (event.deltaY > 0) {
    thumbsSwiper.value.slideNext();
  } else {
    thumbsSwiper.value.slidePrev();
  }
}
</script>

<template>
  <div class="image-gallery p-0 p-sm-2" v-if="cargaCompleta">
    <div v-if="props.LoteStore.lote?.multimedia?.length > 0" class="swiper-wrapper-vertical">
      <!-- Swiper principal -->
      <swiper :loop="true" :spaceBetween="10" :navigation="true" :thumbs="{ swiper: thumbsSwiper || null }"
        :modules="modules" class="mySwiper2 bg-image-background rounded-3 w-100">
        <div class="swiper-container-relative position-relative">
          <div class="vistas-label">
            <i class="fa fa-eye me-1"></i> {{ props.LoteStore.lote.visitas ?? 0 }} vistas
          </div>
          <swiper :loop="true" :spaceBetween="10" :navigation="true" :thumbs="{ swiper: thumbsSwiper || null }"
            :modules="modules" class="mySwiper2 bg-image-background rounded-3 w-100">
            <swiper-slide v-for="(item, index) in props.LoteStore.lote?.multimedia" :key="index">
              <template v-if="item.tipo === 'imagen'">
                <div class="zoom-container" @mousemove="handleMouseMove" @mouseleave="resetZoom">
                  <img :src="`${storage_path}/${item.archivo.hash}.${item.archivo.extension}`"
                    class="main-media zoom-pan" :alt="'Imagen ' + (index + 1)" />
                </div>
              </template>
              <template v-else-if="item.tipo === 'video'">
                <video controls autoplay loop playsinline muted class="main-media"
                  :src="`${storage_path}/${item.archivo.hash}.${item.archivo.extension}`"></video>
              </template>
              <template v-else-if="item.tipo === 'video_url'">
                <div class="video-container">
                  <iframe :src="obtenerUrlYoutube(item.url)" frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen></iframe>
                </div>
              </template>
            </swiper-slide>
          </swiper>
        </div>

      </swiper>

      <!-- Miniaturas DESKTOP (vertical con flechas) -->
      <div class="thumbs-column d-none d-md-flex">
        <div class="arrow-btn" @click="scrollThumbs('up')"><i class="fa fa-angle-up"></i></div>

        <swiper @swiper="setThumbsSwiper" direction="vertical" :loop="false" :spaceBetween="4" :slidesPerView="4"
          :freeMode="true" :watchSlidesProgress="true" :modules="modules" class="thumbs-swiper" @wheel="onThumbsScroll">
          <swiper-slide v-for="(item, index) in multimediaWithPlaceholders"
            :key="item.placeholder ? item.id : 'thumb-' + index">
            <template v-if="!item.placeholder">
              <img v-if="item.tipo === 'imagen'" :src="`${storage_path}/${item.archivo.hash}.${item.archivo.extension}`"
                class="thumbnail" />
              <video v-else-if="item.tipo === 'video'"
                :src="`${storage_path}/${item.archivo.hash}.${item.archivo.extension}`" class="thumbnail" muted></video>
              <div v-else-if="item.tipo === 'video_url'" class="thumbnail iframe-thumbnail">
                <iframe :src="obtenerUrlYoutube(item.url)" frameborder="0"></iframe>
              </div>
            </template>
          </swiper-slide>
        </swiper>

        <div class="arrow-btn" @click="scrollThumbs('down')"><i class="fa fa-angle-down"></i></div>
      </div>

      <!-- Miniaturas MOBILE (horizontal) -->
      <swiper v-if="multimediaWithPlaceholders.length" class="d-flex d-md-none thumbs-swiper-horizontal mt-2"
        :slides-per-view="4" :space-between="10" :modules="[Thumbs]" :watchSlidesProgress="true">
        <swiper-slide v-for="(item, index) in multimediaWithPlaceholders"
          :key="'mobile-' + (item.placeholder ? item.id : index)">
          <template v-if="!item.placeholder">
            <img v-if="item.tipo === 'imagen'" :src="`${storage_path}/${item.archivo.hash}.${item.archivo.extension}`"
              class="thumbnail" />
            <video v-else-if="item.tipo === 'video'"
              :src="`${storage_path}/${item.archivo.hash}.${item.archivo.extension}`" class="thumbnail" muted></video>
            <div v-else-if="item.tipo === 'video_url'" class="thumbnail iframe-thumbnail">
              <iframe :src="obtenerUrlYoutube(item.url)" frameborder="0"></iframe>
            </div>
          </template>
        </swiper-slide>
      </swiper>
    </div>

    <div v-else style="height: 400px;" class="d-flex justify-content-center align-items-center">
      <img :src="imagenDefault" alt="Sin imágenes" class="main-media" />
    </div>
  </div>
</template>

<style>
.image-gallery {
  text-align: center;
  width: 100%;
}

.main-media {
  width: 100%;
  height: 520px;
  object-fit: contain;
  border-radius: 8px;
}

.zoom-container {
  overflow: hidden;
  width: 100%;
  height: 520px;
  position: relative;
}

.zoom-pan {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.2s ease, transform-origin 0.2s ease;
  transform: scale(1);
  transform-origin: center center;
}

.video-container {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  background-color: #000;
  border-radius: 8px;
}

.video-container iframe {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 0;
  top: 0;
  left: 0;
}

/* Estructura */
.swiper-wrapper-vertical {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.thumbs-column {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.thumbs-swiper {
  width: 150px;
  height: 500px;
}

.thumbs-swiper-horizontal {
  width: 100%;
}

/* Miniaturas */
.thumbnail,
.iframe-thumbnail {
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
  border: 2px solid transparent;
  transition: border 0.2s ease;
}

.iframe-thumbnail {
  overflow: hidden;
  background-color: #000;
}

.iframe-thumbnail iframe {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 8px;
}

/* Activa */
.swiper-slide-thumb-active .thumbnail,
.swiper-slide-thumb-active .iframe-thumbnail {
  border-color: #007bff;
}

/* Flechas */
.arrow-btn {
  background: white;
  border-radius: 6px;
  width: 32px;
  height: 32px;
  font-size: 18px;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
}

.arrow-btn:hover {
  background: #f5f5f5;
}

.swiper-container-relative {
  position: relative;
}

.vistas-label {
  position: absolute;
  top: 10px;
  left: 10px;
  background-color: #e60000;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 6px;
  z-index: 10;
  display: inline-flex;
  align-items: center;
}



/* Responsive */
@media (max-width: 768px) {
  .swiper-wrapper-vertical {
    flex-direction: column;
    align-items: center;
  }

  .thumbs-swiper {
    display: none !important;
  }

  .thumbnail,
  .iframe-thumbnail {
    width: 5rem;
    height: 5rem;
  }

  .main-media,
  .zoom-container,
  .video-container {
    height: 400px;
  }
}
</style>
